<template>
  <v-container fluid>
    <div>
      <loading-overlay :visible="!loaded" :label="$t('app.states.loading')" />
      <div class="error" v-if="irregular">
        <p
          v-html="
            `${$t('error.general.irregular-value')} ${$moment(
              irregular[0].createdAt
            ).format('D/M')}`
          "
        ></p>
      </div>

      <div v-if="loaded" class="information">
        <template v-if="$store.getters.patient">
          <p>{{$t('dash.patient.create.form-name')}}: {{$store.getters.patient.firstName}} {{$store.getters.patient.lastName}}</p>
          <p>{{$t('dash.patient.create.form-date')}}: {{ $moment($store.getters.patient.birthday).format("LL") }}</p>
        </template>
        <div class="title">
          <img
            class="icon"
            :src="require('@/assets/icons/kilo-pink.svg')"
            alt="A weight."
          />
          <h2>{{ $t("app.evolution.weight") }}</h2>
          <IconLink
            @click.native="$modal.show('add-modal')"
            :title="$t('app.evolution.action-add')"
            icon="plus-pink"
            class="pink--text"
            color="#e57378"
          ></IconLink>
        </div>
        <p class="stat">
          {{ $t("app.evolution.init") }}:
          <span class="pink--text">{{ first(weightDataset) }}g</span>
        </p>
        <p class="stat">
          {{ $t("app.evolution.now") }}:
          <span class="pink--text">{{ last(weightDataset) }}g</span>
        </p>
      </div>

      <div>
        <EvolutionChart
          v-if="loaded"
          class="chart"
          :chartData="weightDataCollection"
          :options="weightOptions"
          @clicked-item="handleClick('weight', $event)"
        />
        <div class="controls">
          <Button
            v-if="loaded && weightChartStep - 5 > 0"
            @click.native="weightChartStep--"
            title="◀"
            shadow="true"
            color="#e57378"
            bgcolor="#fff"
            width="40px"
            margin="0"
            padding="0"
            class="control prev"
          ></Button>
          <Button
            v-if="loaded && weightChartStep < weightDataset.length"
            @click.native="weightChartStep++"
            title="▶"
            shadow="true"
            color="#e57378"
            bgcolor="#fff"
            width="40px"
            margin="0"
            padding="0"
            class="control next"
          ></Button>
        </div>
      </div>

      <div v-if="loaded" class="information">
        <div class="title">
          <img
            class="icon"
            :src="require('@/assets/icons/ruler-pink.svg')"
            alt="A weight."
          />
          <h2>{{ $t("app.evolution.length") }}</h2>
          <IconLink
            @click.native="$modal.show('add-modal')"
            :title="$t('app.evolution.action-add')"
            icon="plus-pink"
            class="pink--text"
            color="#e57378"
          ></IconLink>
        </div>
        <p class="stat">
          {{ $t("app.evolution.init") }}:
          <span class="pink--text">{{ first(lengthDataset) }}cm</span>
        </p>
        <p class="stat">
          {{ $t("app.evolution.now") }}:
          <span class="pink--text">{{ last(lengthDataset) }}cm</span>
        </p>
      </div>

      <div>
        <EvolutionChart
          v-if="loaded"
          class="chart"
          :chartData="lengthDataCollection"
          :options="lengthOptions"
          @clicked-item="handleClick('length', $event)"
        />
        <div class="controls">
          <Button
            v-if="loaded && lengthChartStep - 5 > 0"
            @click.native="lengthChartStep--"
            title="◀"
            shadow="true"
            color="#e57378"
            bgcolor="#fff"
            width="40px"
            margin="0"
            padding="0"
            class="control prev"
          ></Button
          ><Button
            v-if="loaded && lengthChartStep < lengthDataset.length"
            @click.native="lengthChartStep++"
            title="▶"
            shadow="true"
            color="#e57378"
            bgcolor="#fff"
            width="40px"
            margin="0"
            padding="0"
            class="control next"
          ></Button>
        </div>
      </div>

      <modals-container
        @edit="openEditModal"
        @delete="deleteValue"
        @edited="editValue"
      />

      <modal class="biodata" name="add-modal" width="90%" height="auto">
        <Button
          class="close"
          @click.native="$modal.hide('add-modal')"
          margin="10px"
          color="#31b9be"
          bgcolor="transparent"
          width="30px"
          padding="0"
          icon="close-blue"
        ></Button>
        <div class="error" v-if="error">
          <p>{{ error }}</p>
        </div>

        <p>{{ $t("dash.checklist.form-weight") }}</p>
        <ValueSelector
          type="weight"
          :negative="false"
          @change="
            error = null;
            form.weight = $event;
          "
          @nan="error = $t('error.biodata.not-numeric')"
          @negative="error = $t('error.biodata.not-numeric')"
        />

        <p>{{ $t("dash.checklist.form-length") }}</p>
        <ValueSelector
          type="length"
          :negative="false"
          @change="
            error = null;
            form.length = $event;
          "
          @nan="error = $t('error.biodata.not-numeric')"
          @negative="error = $t('error.biodata.not-numeric')"
        />

        <Button
          :title="$t('app.evolution.action-submit')"
          shadow="true"
          @click.native="validateBiodata"
          margin="1rem 0"
        ></Button>
      </modal>
    </div>
  </v-container>
</template>

<script>
import { neoparentPatients } from "@/api";
import ListItem from "@/page_elements/ListItem";
import Button from "@/page_elements/Button";
import InputField from "@/page_elements/InputField";

export default {
  name: "neoparent-evolution",
  props: [],
  components: {
    ListItem,
    Button,
    InputField
  },
  data() {
    return {
      form: {
        weight: null,
        length: null
      },
      error: null,
      loaded: false,
      entries: null,
      weightDataset: null,
      lengthDataset: null,
      weightLabels: null,
      lengthLabels: null,
      weightChartStep: 0,
      lengthChartStep: 0,
      options: {
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                maxTicksLimit: 10,
                max: 500
              }
            }
          ],
          xAxes: [
            {
              ticks: {
                beginAtZero: true,
                maxTicksLimit: 5
              }
            }
          ]
        },
        legend: {
          position: "top",
          align: "end"
        }
      }
    };
  },
  mounted() {
    this.getBiodata();
  },

  computed: {
    irregular() {
      if (this.entries && this.$store.getters.user.role === "n") {
        let irregularities = this.entries.filter(entry => entry.irregularity);
        if (irregularities.length) {
          return irregularities;
        } else {
          return null;
        }
      }
      return null;
    },
    weightDataCollection() {
      // Weight data
      let start = 0;
      let end = 0;
      if (this.weightDataset.length <= 5) {
        start = 0;
        end = 5;
      } else if (this.weightChartStep - 5 < 0) {
        start = 0;
        end = this.weightChartStep;
      } else {
        start = this.weightChartStep - 5;
        end = this.weightChartStep;
      }
      let weightLabelsSlice = this.weightLabels.slice(start, end);
      let weightDatasetSlice = this.weightDataset.slice(start, end);
      return {
        labels: weightLabelsSlice,
        datasets: [
          {
            data: weightDatasetSlice,
            label: `${this.$t("app.evolution.weight")} (g)`,
            backgroundColor: "rgba(175, 223, 211, 0.6)",
            borderColor: "rgba(89, 164, 145, 0.8)",
            borderWidth: 2,
            spanGaps: true
          }
        ]
      };
    },
    lengthDataCollection() {
      // Length data
      let start = 0;
      let end = 0;
      if (this.lengthDataset.length <= 5) {
        start = 0;
        end = 5;
      } else if (this.lengthChartStep - 5 < 0) {
        start = 0;
        end = this.lengthChartStep;
      } else {
        start = this.lengthChartStep - 5;
        end = this.lengthChartStep;
      }
      let lengthLabelsSlice = this.lengthLabels.slice(start, end);
      let lengthDatasetSlice = this.lengthDataset.slice(start, end);
      return {
        labels: lengthLabelsSlice,
        datasets: [
          {
            data: lengthDatasetSlice,
            label: `${this.$t("app.evolution.length")} (cm)`,
            backgroundColor: "rgba(175, 223, 211, 0.6)",
            borderColor: "rgba(89, 164, 145, 0.8)",
            borderWidth: 2,
            spanGaps: true
          }
        ]
      };
    },
    weightOptions() {
      // Deep copy of options
      let weightOptions = JSON.parse(JSON.stringify(this.options));
      // Calculate max value
      let max = Math.max(...this.weightDataset);
      console.log(max)
      weightOptions.scales.yAxes[0].ticks.max = Math.ceil(max/5)*5;
      weightOptions.scales.yAxes[0].ticks.stepSize = Math.ceil(max/5)*5 / 5
      return weightOptions;
    },
    lengthOptions() {
      // Deep copy of options
      let lengthOptions = JSON.parse(JSON.stringify(this.options));
      // Calculate max value
      let max = Math.max(...this.lengthDataset);
      lengthOptions.scales.yAxes[0].ticks.max = Math.ceil(max/5)*5;
      lengthOptions.scales.yAxes[0].ticks.stepSize = Math.ceil(max/5)*5 / 5
      return lengthOptions;
    }
  },
  methods: {
    getBiodata() {
      neoparentPatients
        .getAllBioDataEntriesForPatient(this.$store.getters.patient.id)
        .then(result => {
          this.consoleLog(result);
          this.entries = result.data.biodataEntries;
          this.consoleLog(this.entries);

          // Get values
          this.weightDataset = this.entries
            .filter(x => x.weight)
            .map(e => e.weight);
          this.weightChartStep = this.weightDataset.length;
          this.consoleLog(this.weightDataset);
          this.lengthDataset = this.entries
            .filter(x => x.length)
            .map(e => e.length);
          this.lengthChartStep = this.lengthDataset.length;
          this.consoleLog(this.lengthDataset);

          // Get dates
          this.weightLabels = this.entries
            .filter(x => x.weight)
            .map(e => this.$moment(e.createdAt).format("D/M"));
          this.consoleLog(this.weightLabels);
          this.lengthLabels = this.entries
            .filter(x => x.length)
            .map(e => this.$moment(e.createdAt).format("D/M"));
          this.consoleLog(this.lengthLabels);

          // Initiala data collection
          this.loaded = true;
        })
        .catch(err => {
          this.consoleLog(err);
          this.error = err.response.data.message;
        });
    },
    first(arr) {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] && arr[i] !== 0) {
          return arr[i];
        }
      }
      return null;
    },
    last(arr) {
      for (let i = arr.length; i > 0; i--) {
        if (arr[i] && arr[i] !== 0) {
          return arr[i];
        }
      }
      return null;
    },
    numberOrNull(n) {
      if (isNaN(n)) {
        return null;
      } else return Number(n);
    },
    validateBiodata() {
      let normalValues = true;
      // Validate new weight is within range of +/- 20grams
      if (this.form.weight) {
        normalValues =
          this.form.weight >= this.last(this.weightDataset) - 1000 &&
          this.form.weight <= this.last(this.weightDataset) + 1000;
      }
      // Validate new length is within range of +/- 10cm
      if (this.form.length) {
        normalValues =
          this.form.length >= this.last(this.lengthDataset) - 100 &&
          this.form.length <= this.last(this.lengthDataset) + 100;
      }
      // Check for input
      if (this.form.weight || this.form.length) {
        if (!normalValues) {
          // Strange values detected, alert nurses
          this.submitBiodata(true);
        }
        this.submitBiodata(false);
      }
    },
    submitBiodata(irreg) {
      neoparentPatients
        .addBioDataEntry(this.$store.getters.user.patient.id, {
          weight: this.form.weight,
          length: this.form.length,
          irregularity: irreg
        })
        .then(result => {
          this.consoleLog(result);
          this.$modal.hide("add-modal");
          this.getBiodata();
        })
        .catch(err => {
          this.consoleLog(err);
          this.error = this.$t("error.general.connection");
        });
      this.form.weight = null;
      this.form.length = null;
    },
    handleClick(type, index) {
      console.log(type);
      console.log(index);
      let stepIndex = index
/*        type === "weight"
          ? this.weightChartStep - 5 + index
          : this.lengthChartStep - 5 + index;*/
      this.$modal.show(
        {
          template: `
          <div>
          <p>{{ $t('app.evolution.value') }}: <span class="pink--text">{{ value }}{{ type === 'weight' ? 'g' :'cm' }}</span></p>
          <p>{{ $t('app.evolution.date') }}: <span class="pink--text">{{ date }}</span></p>
            <ListItem
              border="1px solid #31b9be"
              @click.native="$emit('edit', {type, value, date}); $modal.hide($parent.name);"
              >{{ $t('app.evolution.edit') }}</ListItem
            >
            <ListItem
              border="1px solid #31b9be"
              last="true"
              @click.native="$emit('delete', {type, value, date}); $modal.hide($parent.name);"
              >{{ $t('app.evolution.delete') }}</ListItem
            >
          </div>
          `,
          components: {
            ListItem
          },
          props: {
            type: {},
            value: {},
            date: {}
          }
        },
        {
          type: type,
          value:
            type === "weight"
              ? this.weightDataset[stepIndex]
              : this.lengthDataset[stepIndex],
          date:
            type === "weight"
              ? this.weightLabels[stepIndex]
              : this.lengthLabels[stepIndex]
        },
        {
          height: "auto",
          width: "100%",
          class: "menu"
        }
      );
    },
    openEditModal(oldValue) {
      this.$modal.show(
        {
          template: `
          <div class="center">

            <h3>{{ $t('app.evolution.edit') }}</h3>
            <div class="error" v-if="error">
              <p>{{ error }}</p>
            </div>
            <InputField
              v-model="newValue"
              required
              :placeholder="$t('app.evolution.form-value')"
              type="number"
            ></InputField>
            <Button
              :title="$t('app.evolution.action-edit')"
              shadow="true"
              @click.native="$emit('edited', {oldValue, newValue}); $modal.hide($parent.name);"
              margin="1rem 0"
            ></Button>
          </div>
          `,
          components: {
            InputField,
            Button
          },
          data() {
            return {
              newValue: null,
              error: null
            };
          },
          props: {
            oldValue: {}
          }
        },
        {
          oldValue: oldValue
        },
        {
          height: "auto",
          width: "90%",
          class: "comment"
        }
      );
    },
    editValue(event) {
      const entryToEdit = this.entries.find(
        e =>
          e[event.oldValue.type] == event.oldValue.value &&
          this.$moment(e.createdAt).format("D/M") == event.oldValue.date
      );
      this.consoleLog(entryToEdit.id);
      console.log("event", event)

      neoparentPatients
        .editBioDataEntry(entryToEdit.id, event.oldValue.type, event.newValue)
        .then(() => {
          this.getBiodata();
        })
        .catch(err => {
          this.consoleLog(err);
        });
    },
    deleteValue(event) {
      console.log("event", event)
      const entryToDelete = this.entries.find(
        e =>
          e[event.type] == event.value &&
          this.$moment(e.createdAt).format("D/M") == event.date
      );

      if (confirm(this.$t("app.evolution.confirm-delete"))) {
        neoparentPatients
          .deleteBioDataEntry(entryToDelete.id, event.type)
          .then(() => {
            this.getBiodata();
          })
          .catch(err => {
            this.consoleLog(err);
          });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.chart {
  width: 90%;
  height: 300px;
  margin: 16px auto;
}

.controls {
  width: 90%;
  display: flex;
  margin: 0 auto 16px auto;
  justify-content: space-between;

  .next {
    margin-left: auto !important;
  }
}

.information {
  margin: 0 5% 2rem;

  .stat {
    margin-left: 10px;
  }

  .title {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    h2 {
      margin-bottom: 1rem;
    }

    .icon {
      max-width: 48px;
      margin: 0;
    }

    :last-child {
      margin-left: auto;
    }
  }

  p {
    margin: 0 0 0.5rem;
    text-align: left;
  }
}
</style>
